// useTaskLock
// -----------
// Custom React hook — owns the "Take-On" lock for a request. Staff users must
// claim a request before working on it; this hook loads the active request_task
// on mount and exposes a handler to claim it.
// TypeScript source — useTaskLock.js is a thin re-export stub.
//
// Returns { taskLock, taskLockLoading, takingOn, handleTakeOn }
//   taskLock — null = loading, false = no lock, task object = locked

import React, { useState } from "react";
import { findActiveLockTask, updateResponderTasksByRequest } from "../data/requestTasks";

interface TaskRow {
  id: string;
  assigned_to?: string | null;
  assigned_to_id?: string | null;
  assigned_at?: string | null;
  type?: string;
  completed_at?: string | null;
}
interface TakeOnUser {
  id?: string | null;
  shelterName?: string | null;
}

export function useTaskLock(
  requestId: number | string | null | undefined,
  isStaff: boolean,
  user: TakeOnUser
) {
  const [taskLock, setTaskLock] = useState<TaskRow | false | null>(null);
  const [taskLockLoading, setTaskLockLoading] = useState(true);
  const [takingOn, setTakingOn] = useState(false);
  // The assignee's name when the active task is held by SOMEONE ELSE (not me).
  // null = unassigned, mine, or no active task. The ownership gate in RequestDetail
  // uses this to block a peer responder from opening another responder's request.
  const [assignedToOther, setAssignedToOther] = useState<string | null>(null);

  React.useEffect(
    function () {
      if (!isStaff || !requestId) {
        setTaskLockLoading(false);
        setTaskLock(false);
        setAssignedToOther(null);
        return;
      }
      // Find the active (uncompleted) task for this request
      findActiveLockTask(requestId).then(function (task) {
        setTaskLockLoading(false);
        if (task) {
          if (task.assigned_to) {
            // Already taken on — no lock. Flag it if held by someone other than me.
            setTaskLock(false);
            // Ownership strictly by assignee id (db-201/202: assigned_to text is a
            // derived cache; the old `=== user.shelterName` fallback is gone).
            const mine = !!task.assigned_to_id && task.assigned_to_id === user.id;
            setAssignedToOther(mine ? null : task.assigned_to);
          } else {
            setTaskLock(task);
            setAssignedToOther(null);
          }
        } else {
          // No active task — no lock needed
          setTaskLock(false);
          setAssignedToOther(null);
        }
      });
    },
    [requestId, isStaff]
  );

  async function handleTakeOn() {
    // biome-ignore lint/complexity/useOptionalChain: taskLock is `TaskRow | false | null`; `taskLock?.id` lets `false.id` through and tsc errors ("Property 'id' does not exist on type 'false'"). Keep the explicit `||` guard so the `false` case short-circuits before `.id`.
    if (!taskLock || !taskLock.id || !requestId) return;
    setTakingOn(true);
    const now = new Date().toISOString();
    // Assign to Me is request-level: claim EVERY open responder task of this request,
    // not just the active lock task, so the responder holds the whole request (and
    // db-121 then attaches any later comment/file task to them too).
    const { error } = await updateResponderTasksByRequest(
      requestId,
      // assigned_to (text) is derived server-side from assigned_to_id (db-202).
      { assigned_to_id: user.id, assigned_at: now },
      "open"
    );
    if (!error) {
      setTaskLock(false);
    }
    setTakingOn(false);
  }

  return { taskLock, taskLockLoading, takingOn, handleTakeOn, assignedToOther };
}
